import { type FC, type ReactNode, useMemo, useEffect, useState } from 'react';
import {
    createBrowserRouter,
    createHashRouter,
    createRoutesFromElements,
    RouterProvider,
    Route,
    Outlet,
} from 'react-router-dom';
import { ErrorBoundary, type FallbackProps } from 'react-error-boundary';
import hideSplashScreen from 'hideSplashScreen';
import { angularInjectorProvider } from 'Injector';
import { HeroUIProvider } from '@heroui/react';
import { LoadingBoundary } from 'LoadingBoundary';
import { useSelector } from 'react-redux';
import { getAdminRoutes, configureAdminRoutes } from 'Admin';
import { getLessonPlayerRoutes, configureLessonPlayerRoutes } from 'LessonPlayer';
import { getAuthenticationRoutes, configureAuthenticationRoutes } from 'Authentication';
import {
    getOnboardingRoutes,
    configureOnboardingRoutes,
    getForgotPasswordRoutes,
    configureForgotPasswordRoutes,
} from 'Onboarding';
import { getDisconnectedMobileInitRoutes, configureDisconnectedMobileInitRoutes } from 'DisconnectedMobileInit';
import { getGradingPortalRoutes, configureGradingPortalRoutes } from 'GradingPortal';
import { getResourcesRoutes, configureResourcesRoutes } from 'Resources';
import { getTutorBotAiAdvisorRoutes, configureTutorBotAiAdvisorRoutes } from 'TutorBotAiAdvisorChat';
import { getStudentDashboardRoutes, configureStudentDashboardRoutes } from 'StudentDashboard';
import { getChooseInstitutionRoutes, configureChooseInstitutionRoutes } from 'Institutions/ChooseInstitution/Routes';
import { getCoachRoutes, configureCoachRoutes } from 'TutorBotCoach';
import { getBrowseCoursesRoutes, configureBrowseCoursesRoutes } from 'BrowseCourses';
import { getStudentNetworkV2Routes, configureStudentNetworkV2Routes } from 'StudentNetworkV2';
import { getSettingsRoutes, configureSettingsRoutes } from 'Settings';
import { getReportsRoutes, configureReportsRoutes } from 'Reports';
import {
    getStreamDashboardRoutes,
    configureStreamDashboardRoutes,
    useTrackLearningSectionBreadcrumbs,
} from 'StreamDashboard';
import { getDeferralLinkRoutes, configureDeferralLinkRoutes } from 'DeferralLink';
import { loadGlobalDependencies } from 'AssetLoader';
import { getEditorRoutes, configureEditorRoutes } from 'Editor';
import { registerRoutes } from 'FrontRoyalRouteRegistry';
import { AppShell } from 'AppShell';
import { AppToastProvider } from 'Toast';
import { SendBirdProvider } from '@sendbird/uikit-react';
import { useMessagingConfig } from 'Messaging/utils/useMessagingConfig';
import { SendbirdConnectedContext } from 'Messaging/components/MessagingProvider';
import FrontRoyalSpinner from 'FrontRoyalSpinner';
import { selectHardRedirectInProgress } from 'Navigation';
import { useSetupReactRouterBridge } from './setupReactRouterBridge';
import { useTrackLastVisitedRoute } from './useTrackLastVisitedRoute';
import { RootRedirect, HomeRedirect } from './HomeRedirect';
import { useInitializeReactRouterProvider } from './useInitializeReactRouterProvider';

const ReloadTo404: FC = () => {
    useEffect(() => {
        window.location.replace('/404');
    }, []);

    return null;
};

// Register route metadata
registerRoutes(configureAdminRoutes());
registerRoutes(configureLessonPlayerRoutes());
registerRoutes(configureAuthenticationRoutes());
registerRoutes(configureOnboardingRoutes());
registerRoutes(configureForgotPasswordRoutes());
registerRoutes(configureDisconnectedMobileInitRoutes());
registerRoutes(configureResourcesRoutes());
registerRoutes(configureTutorBotAiAdvisorRoutes());
registerRoutes(configureStudentDashboardRoutes());
registerRoutes(configureChooseInstitutionRoutes());
registerRoutes(configureCoachRoutes());
registerRoutes(configureBrowseCoursesRoutes());
registerRoutes(configureStudentNetworkV2Routes());
registerRoutes(configureSettingsRoutes());
registerRoutes(configureReportsRoutes());
registerRoutes(configureEditorRoutes());
registerRoutes(configureStreamDashboardRoutes());
registerRoutes(configureDeferralLinkRoutes());
registerRoutes(configureGradingPortalRoutes());

const routeArray = createRoutesFromElements(
    <Route
        path="/"
        element={<Outlet />}
        loader={async () => {
            await loadGlobalDependencies();
            return null;
        }}
    >
        {getAuthenticationRoutes()}
        {getOnboardingRoutes()}
        {getForgotPasswordRoutes()}
        {getDisconnectedMobileInitRoutes()}
        {getResourcesRoutes()}
        {getTutorBotAiAdvisorRoutes()}
        {getStudentDashboardRoutes()}
        {getChooseInstitutionRoutes()}
        {getCoachRoutes()}
        {getBrowseCoursesRoutes()}
        {getStudentNetworkV2Routes()}
        {getSettingsRoutes()}
        {getAdminRoutes()}
        {getReportsRoutes()}
        {getLessonPlayerRoutes()}
        {getStreamDashboardRoutes()}
        {getDeferralLinkRoutes()}
        {getEditorRoutes()}
        {getGradingPortalRoutes()}
        <Route path="home" element={<HomeRedirect />} />
        <Route index element={<RootRedirect />} />
        <Route path="*" element={<ReloadTo404 />} />
    </Route>,
);

// This serves as a catch-all for errors that occur during route resolution. It hides the splash
// screen so a failed route does not leave it up, then rethrows the error so that the error boundary
// in the LoadingBoundary can display an appropriate message.
const HandleErrorOnRouteResolution: FC<FallbackProps> = ({ error }) => {
    hideSplashScreen(angularInjectorProvider.requireInjector());
    throw error;
};

function SendbirdConnectionProvider({ children }: { children: ReactNode }) {
    const { appId, accessToken, userId, colorSet } = useMessagingConfig();
    const [connected, setConnected] = useState(false);

    if (!appId || !userId || !accessToken) {
        return <SendbirdConnectedContext.Provider value={false}>{children}</SendbirdConnectedContext.Provider>;
    }

    return (
        <SendBirdProvider
            appId={appId}
            userId={userId}
            accessToken={accessToken}
            colorSet={colorSet}
            breakpoint="767px"
            eventHandlers={{
                connection: {
                    onConnected: () => setConnected(true),
                },
            }}
        >
            <SendbirdConnectedContext.Provider value={connected}>{children}</SendbirdConnectedContext.Provider>
        </SendBirdProvider>
    );
}

function RoutingRoot() {
    useTrackLastVisitedRoute();
    useSetupReactRouterBridge();
    const hardRedirectInProgress = useSelector(selectHardRedirectInProgress);
    useTrackLearningSectionBreadcrumbs();
    const isRouterProviderReady = useInitializeReactRouterProvider();
    const shouldShowLoadingState = hardRedirectInProgress || !isRouterProviderReady;

    const content = shouldShowLoadingState ? (
        <div className="flex h-full items-center justify-center">
            <FrontRoyalSpinner />
        </div>
    ) : (
        <HeroUIProvider className="hero h-full">
            <LoadingBoundary>
                <ErrorBoundary FallbackComponent={HandleErrorOnRouteResolution}>
                    <Outlet />
                </ErrorBoundary>
            </LoadingBoundary>
        </HeroUIProvider>
    );

    // SendbirdConnectionProvider wraps AppShell so both the header
    // (AppHeaderReactWrapper) and route content share one SDK connection.
    return (
        <SendbirdConnectionProvider>
            <AppShell>{content}</AppShell>
            {/* The single, app-wide HeroUI toast region. It sits in its own HeroUIProvider as a
                sibling of AppShell (not inside `content`) so it is never remounted by route
                animation or shell-visibility toggles — HeroUI's toast queue is global, but the
                provider that renders it must be a persistent singleton. Toasts are brand-themed
                via the .quantic/.valar class on <body>, so no BrandContainer is needed here. */}
            <HeroUIProvider>
                <AppToastProvider />
            </HeroUIProvider>
        </SendbirdConnectionProvider>
    );
}

export function FrontRoyalRouter() {
    const router = useMemo(() => {
        const routerFn = window.CORDOVA ? createHashRouter : createBrowserRouter;
        return routerFn([
            {
                path: '/',
                id: 'root',
                element: <RoutingRoot />,
                children: routeArray, // Assuming `routeArray` contains your child routes
            },
        ]);
    }, []);

    return (
        <div>
            <RouterProvider router={router} />
        </div>
    );
}

export default FrontRoyalRouter;
